import { IPoint2d } from '@/editor/math/IPoint2d';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { calculatePolygonArea } from '@/editor/model/polygon/utils';
import { getUniqueId } from '@/editor/utils';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { formatLinearDimensionPoint } from './utils';

interface IDimension {
  id: string;
}

export enum EAreaDimensionType {
  RECT = 0,
  POLYGON = 1
}

export interface IDimensionPoint extends IPoint2d {
  id: string;
}

export interface IAreaDimension extends IDimension {
  points: IDimensionPoint[];
  area: number;
  labelPosition: IPoint2d;
  type: EAreaDimensionType;
}

export interface ILinearDimension extends IDimension {
  start: IPoint2d;
  end: IPoint2d;
  offset: number;
}

export function createAreaDimension(points: IPoint2d[], isRect: boolean): IAreaDimension {
  const area = Number((Math.abs(calculatePolygonArea(points)) / (1000 * 1000)).toFixed(2));

  return {
    id: getUniqueId(),
    area,
    type: isRect ? EAreaDimensionType.RECT : EAreaDimensionType.POLYGON,
    labelPosition: getAreaDimensionLabelPosition(points),
    points: toCounterClockwise(
      points.map((point) => ({
        ...point,
        id: getUniqueId()
      }))
    )
  };
}

export function updateAreaDimension(points: IDimensionPoint[]): Partial<IAreaDimension> {
  return {
    area: Number((Math.abs(calculatePolygonArea(points)) / (1000 * 1000)).toFixed(2)),
    labelPosition: getAreaDimensionLabelPosition(points),
    points
  };
}

export function createLinearDimension(points: IPoint2d[]): ILinearDimension {
  const { startPoint, endPoint, offset } = formatLinearDimensionPoint(points);

  return {
    id: getUniqueId(),
    start: startPoint,
    end: endPoint,
    offset
  };
}

function getAreaDimensionLabelPosition(points: IPoint2d[]): IPoint2d {
  if (points.length < 3) {
    return getPointsCenter(points);
  }

  try {
    const centroid = Curve2dUtils.calculatePolygonCentroid(points);
    if (Number.isFinite(centroid.x) && Number.isFinite(centroid.y)) {
      return centroid;
    }
  } catch {
    // 面积标注创建时仅使用几何中心兜底，避免异常多边形阻断标注生成。
  }

  return getPointsCenter(points);
}

function getPointsCenter(points: IPoint2d[]): IPoint2d {
  if (!points.length) {
    return { x: 0, y: 0 };
  }

  const box = points.reduce(
    (result, point) => ({
      minX: Math.min(result.minX, point.x),
      minY: Math.min(result.minY, point.y),
      maxX: Math.max(result.maxX, point.x),
      maxY: Math.max(result.maxY, point.y)
    }),
    {
      minX: points[0].x,
      minY: points[0].y,
      maxX: points[0].x,
      maxY: points[0].y
    }
  );

  return {
    x: (box.minX + box.maxX) / 2,
    y: (box.minY + box.maxY) / 2
  };
}

/**
 * 计算多边形有符号面积（Shoelace 公式）
 * 返回正值 → 逆时针
 * 返回负值 → 顺时针
 */
function signedArea(points: IPoint2d[]): number {
  const n = points.length;
  let area = 0;

  for (let i = 0; i < n; i++) {
    const curr = points[i];
    const next = points[(i + 1) % n];
    area += (next.x - curr.x) * (next.y + curr.y);
  }

  return -area / 2;
}
/**
 * 将多边形顶点调整为逆时针方向
 */
function toCounterClockwise(points: IDimensionPoint[]): IDimensionPoint[] {
  if (points.length < 3) {
    return [...points];
  }

  const result = [...points];

  if (signedArea(result) < 0) {
    result.reverse();
  }

  return result;
}

export function getLinearDimensionRulerLine({ start, end, offset }: ILinearDimension) {
  const seg = new LineSeg2d(start, end);

  const verticalDirection = seg.getPerpendicularDirectionVector();
  const rulerLineStart = Point2dUtils.addDistance(start, offset, verticalDirection);
  const rulerLineEnd = Point2dUtils.addDistance(end, offset, verticalDirection);

  return { start: rulerLineStart, end: rulerLineEnd };
}
